import { Component } from '@theme/component';

/**
 * "Share this" popover on the blog article (hz/80). Toggled by the trigger button; closes on a second click,
 * a click outside, or Escape. Never moves focus (as today).
 *
 * @extends {Component<{ trigger: HTMLButtonElement, panel: HTMLElement }>}
 */
class ZerbayShare extends Component {
  requiredRefs = ['trigger', 'panel'];

  #open = false;
  #frame = 0;
  /** @type {(() => void) | null} Cancels a running close (its timer and transitionend listener). */
  #cancelClose = null;

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#close();
  };

  toggle() {
    if (this.#open) this.#close();
    else this.#show();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Leave a fully closed popover behind (editor re-render, morph): no stale open state without its listeners.
    this.#removeListeners();
    cancelAnimationFrame(this.#frame);
    this.#cancelClose?.();
    this.#open = false;
    const { trigger, panel } = this.refs;
    trigger?.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.classList.remove('zb-share__panel--visible');
      panel.hidden = true;
    }
  }

  #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #show() {
    const { trigger, panel } = this.refs;
    this.#cancelClose?.();
    this.#open = true;
    // No transition while the start state is laid out, so an edge alignment never animates sideways (only the 10px rise).
    panel.style.transition = 'none';
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    // Recomputed on every open: centred on the button unless it would leave the viewport. Measured from layout boxes
    // (the root shrink-wraps the button; offsetWidth ignores the translate/opacity animation), so a reopen during the
    // close transition still measures the final position.
    panel.classList.remove('zb-share__panel--start', 'zb-share__panel--end');
    const root = this.getBoundingClientRect();
    const width = panel.offsetWidth;
    const centre = root.left + root.width / 2;
    const left = centre - width / 2;
    const right = centre + width / 2;
    const viewport = document.documentElement.clientWidth;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (left < 0) panel.classList.add(rtl ? 'zb-share__panel--end' : 'zb-share__panel--start');
    else if (right > viewport) panel.classList.add(rtl ? 'zb-share__panel--start' : 'zb-share__panel--end');
    void panel.offsetWidth;
    panel.style.removeProperty('transition');

    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => panel.classList.add('zb-share__panel--visible'));

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
  }

  #close() {
    if (!this.#open) return;
    const { trigger, panel } = this.refs;
    this.#open = false;
    this.#removeListeners();
    cancelAnimationFrame(this.#frame);
    trigger.setAttribute('aria-expanded', 'false');
    panel.classList.remove('zb-share__panel--visible');

    if (this.#reducedMotion()) {
      panel.hidden = true;
      return;
    }

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target === panel) finish();
    };
    const cleanup = () => {
      clearTimeout(timer);
      panel.removeEventListener('transitionend', onEnd);
      this.#cancelClose = null;
    };
    const finish = () => {
      cleanup();
      if (!this.#open) panel.hidden = true;
    };
    panel.addEventListener('transitionend', onEnd);
    const timer = window.setTimeout(finish, 250);
    this.#cancelClose = cleanup;
  }

  #removeListeners() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }
}

if (!customElements.get('zerbay-share')) customElements.define('zerbay-share', ZerbayShare);
